import { useEffect, useState } from 'react';

interface PanelPreferenceState {
  [key: string]: boolean;
}

const STORAGE_KEY = 'secdefcon:panel-expanded-v1';

function readPreferences(): PanelPreferenceState {
  if (typeof window === 'undefined') return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as PanelPreferenceState) : {};
  } catch {
    return {};
  }
}

function writePreferences(state: PanelPreferenceState): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures (private mode, storage quotas, or restrictions).
  }
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function usePersistedPanelState(
  panelId: string,
  initialExpanded: boolean,
): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [expanded, setExpandedRaw] = useState<boolean>(() => {
    const stored = readPreferences()[panelId];
    return isBoolean(stored) ? stored : initialExpanded;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updated = readPreferences();
    updated[panelId] = expanded;
    writePreferences(updated);
  }, [expanded, panelId]);

  const setExpanded = (next: boolean | ((prev: boolean) => boolean)) => {
    setExpandedRaw((prev) => {
      return typeof next === 'function' ? next(prev) : next;
    });
  };

  return [expanded, setExpanded];
}
